import { motion } from "framer-motion";
import { Save } from "lucide-react";
import { GlassCard } from "@/components/GlassCard";

export const Toggle = ({ checked, onChange, label, testId }) => (
  <button
    type="button"
    role="switch"
    aria-checked={!!checked}
    aria-label={label}
    onClick={() => onChange(!checked)}
    data-testid={testId}
    className={`relative h-6 w-11 shrink-0 rounded-full border transition-colors duration-300 ${
      checked ? "border-white bg-white" : "border-white/20 bg-white/[0.05]"
    }`}
  >
    <span
      className={`absolute top-1/2 h-4 w-4 -translate-y-1/2 rounded-full transition-all duration-300 ${
        checked ? "left-[22px] bg-black" : "left-1 bg-paper-400"
      }`}
    />
  </button>
);

export const ToggleRow = ({ label, checked, onChange, testId }) => (
  <div className="flex items-center justify-between gap-4">
    <span className="text-sm text-paper-200">{label}</span>
    <Toggle checked={checked} onChange={onChange} label={label} testId={testId} />
  </div>
);

export const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-paper-500">{label}</span>
    {children}
    {hint && <span className="mt-1 block text-xs text-paper-500/80">{hint}</span>}
  </label>
);

export const inputCls =
  "w-full rounded-xl border border-white/10 bg-ink-800 px-4 py-2.5 text-sm text-paper-100 placeholder:text-paper-500/60 transition-colors duration-300 focus:border-white/30 focus:outline-none";

export const selectCls = `${inputCls} appearance-none`;

export const TextInput = ({ value, onChange, placeholder, testId, type = "text" }) => (
  <input
    type={type}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className={inputCls}
    data-testid={testId}
  />
);

export const NumInput = ({ value, onChange, placeholder, testId, min = 0 }) => (
  <input
    type="number"
    min={min}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
    placeholder={placeholder}
    className={inputCls}
    data-testid={testId}
  />
);

export const Textarea = ({ value, onChange, placeholder, testId, rows = 3 }) => (
  <textarea
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    rows={rows}
    className={`${inputCls} resize-y`}
    data-testid={testId}
  />
);

export const ColorInput = ({ value, onChange, testId }) => (
  <div className="flex items-center gap-3">
    <span
      className="h-10 w-10 shrink-0 rounded-xl border border-white/15"
      style={{ background: /^#[0-9a-fA-F]{6}$/.test(value || "") ? value : "#0A0A0A" }}
      aria-hidden="true"
    />
    <input
      type="text"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder="#FFFFFF"
      className={`${inputCls} font-mono`}
      data-testid={testId}
    />
  </div>
);

const toStr = (value) => (value === null || value === undefined || value === "" ? "" : String(value));

export const ChannelSelect = ({ value, onChange, channels, types, testId }) => {
  const options = channels.filter((c) => types.includes(c.type));
  return (
    <select value={toStr(value)} onChange={(e) => onChange(e.target.value || null)} className={selectCls} data-testid={testId}>
      <option value="">Not set</option>
      {options.map((c) => (
        <option key={c.id} value={String(c.id)}>
          {c.type === "category" ? c.name : `#${c.name}`}
        </option>
      ))}
    </select>
  );
};

export const RoleSelect = ({ value, onChange, roles, testId }) => (
  <select value={toStr(value)} onChange={(e) => onChange(e.target.value || null)} className={selectCls} data-testid={testId}>
    <option value="">Not set</option>
    {[...roles]
      .sort((a, b) => b.position - a.position)
      .map((r) => (
        <option key={r.id} value={String(r.id)}>
          {r.name}
        </option>
      ))}
  </select>
);

export const ChoiceSelect = ({ value, onChange, options, testId }) => (
  <select value={toStr(value)} onChange={(e) => onChange(e.target.value || null)} className={selectCls} data-testid={testId}>
    <option value="">Not set</option>
    {options.map((o) => (
      <option key={o} value={o}>
        {o}
      </option>
    ))}
  </select>
);

export const ModuleCard = ({ icon: Icon, title, description, dirty, saving, onSave, testId, id, children }) => (
  <GlassCard className="flex h-full flex-col p-6 md:p-8" hover={false} data-testid={testId}>
    <div id={id} className="scroll-mt-40">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03]">
          <Icon className="h-5 w-5 text-paper-100" aria-hidden="true" />
        </span>
        <h3 className="font-display text-lg font-semibold text-paper-100">{title}</h3>
      </div>
      {description && <p className="mt-2 text-sm leading-relaxed text-paper-500">{description}</p>}
      <div className="mt-6 flex-1 space-y-4">{children}</div>
      <div className="mt-6 border-t border-white/5 pt-4">
        <button
          onClick={onSave}
          disabled={!dirty || saving}
          data-testid={`${testId}-save`}
          className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-all duration-300 ${
            dirty && !saving
              ? "bg-white text-black hover:scale-[1.02]"
              : "cursor-not-allowed border border-white/10 text-paper-500"
          }`}
        >
          <Save className="h-4 w-4" aria-hidden="true" />
          {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </button>
      </div>
    </div>
  </GlassCard>
);

export const ReadOnlyNote = ({ children, testId }) => (
  <p className="rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3 font-mono text-xs leading-relaxed text-paper-500" data-testid={testId}>
    {children}
  </p>
);

export const SectionNav = ({ sections, active }) => (
  <nav
    className="scrollbar-none sticky top-[72px] z-30 -mx-6 mb-10 flex gap-2 overflow-x-auto border-b border-white/5 bg-black/70 px-6 py-4 backdrop-blur-xl"
    aria-label="Server settings sections"
    data-testid="settings-nav"
  >
    {sections.map((s) => (
      <button
        key={s.id}
        onClick={() => document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth", block: "start" })}
        data-testid={`settings-nav-${s.id}`}
        className={`shrink-0 rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors duration-300 ${
          active === s.id
            ? "border-white bg-white text-black"
            : "border-white/15 text-paper-400 hover:border-white/40 hover:text-paper-100"
        }`}
      >
        {s.label}
      </button>
    ))}
  </nav>
);

export const FadeIn = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 14 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);
